import { LevelDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { SlabFloorAssemblyEntity } from "building-types/floors/slabFloorAssemblyTypes";
import { SlabType } from "building-types/foundations/foundationEnums";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { LevelDef } from "building-types/levels/LevelDef";
import type { Level, GeneratedLevelDatums } from "building-types/levels/levelTypes";
import type { FloorSettings } from "building-types/settings/floorSettings";
import type {
  BasementFoundationSuperSystemSettings,
  FoundationSystemSettings,
} from "building-types/settings/foundationSettingsTypes";
import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { Table, RWTable } from "common/data/table";
import type { WorldInches, LevelInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined, assertIsNumber, ensureDefined } from "common/utils/assertOfType";
import { levelInchesNew, worldInchesNew } from "common/utils/nominalNumbers";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { makeLevelFloorAssembly, floorAssemblyFloorPlateThickness } from "doc/elements/floor/floorAssembly";
import { isGroundLevel, levelNew } from "../elements/level/levelTools";
import { settingsFromLevel } from "../generation/generateLevelAttributesTools";

/**
 * LevelDef with resolved offset
 */
export interface LevelWithResolvedOffset extends LevelDef {
  readonly isStackedLevel: boolean;
  readonly isAboveGround: boolean;
  readonly resolvedOffset: WorldInches;
  readonly stackedAboveLevelId: string | undefined;
}

/**
 * Doubly linked list LevelDef
 */
export interface LinkedLevelWithResolvedOffset extends LevelWithResolvedOffset {
  readonly levelAbove?: string;
  readonly levelBelow?: string;
  readonly floorIndex: number;
}

export function generateLevel(
  levelDef: LinkedLevelWithResolvedOffset,
  levels: Table<Level>,
  levelsFloorSettings: Table<FloorSettings>,
  frameFloorAssemblies: Table<FrameFloorAssemblyEntity>,
  slabFloorAssemblies: Table<SlabFloorAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  foundationSettings?: FoundationSystemSettings,
): Level {
  const floorSettings = levelsFloorSettings[levelDef.id];
  const floorAssembly = makeLevelFloorAssembly({
    floorAssemblyId: floorSettings.floorAssembly,
    floorSettings,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  });

  const { topOfWall } = levelDef;

  assertIsNumber(topOfWall);

  assertIsNotUndefined(floorAssembly);

  const floorPlateThickness = floorAssemblyFloorPlateThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorAssembly,
  );

  const { resolvedOffset: floorElevation, isAboveGround } = levelDef;

  // look for the reference level
  // for above ground levels, this will be the floor below,
  // and for below ground levels this will be the floor above
  const levelBelow = levelDef.levelBelow ? levels[levelDef.levelBelow] : undefined;
  const levelAbove = levelDef.levelAbove ? levels[levelDef.levelAbove] : undefined;
  const refLevel = isAboveGround ? levelBelow : levelAbove;

  const levelDatumsGenerated = generateLevelDatums(
    levelDef,
    worldInchesNew(0), // no instance offset for normal levels
    floorElevation,
    refLevel,
    floorPlateThickness,
    levels,
    foundationSettings,
  );

  return levelNew({
    ...levelDef,
    topOfWall: topOfWall as LevelInches,
    levelDatumsGenerated,
    floorElevation,
    floorPlateThickness,
    floorAssemblyId: floorAssembly.assemblyId,
  });
}

export function generateLevelDatums(
  linkedLevel: LinkedLevelWithResolvedOffset,
  instanceOffset: WorldInches,
  floorElevation: WorldInches,
  refLevel: Level | undefined,
  floorPlateThickness: number,
  generatedLevels: Table<Level>,
  settings?: FoundationSystemSettings,
): GeneratedLevelDatums {
  let grade: LevelInches = levelInchesNew(0);
  let garage: LevelInches = levelInchesNew(0);
  let porch: LevelInches = levelInchesNew(0);
  let deck: LevelInches = levelInchesNew(0);
  let wainscot: LevelInches = levelInchesNew(0);
  let foundation: LevelInches = levelInchesNew(0);
  const roofBottom = linkedLevel.topOfWall;
  const roofTop = linkedLevel.topOfWall;

  assertIsNumber(roofBottom);
  assertIsNumber(roofTop);

  wainscot = levelInchesNew(-floorPlateThickness);

  // only apply settings to ground levels, otherwise use the last floor as a reference
  if (
    settings &&
    isGroundLevel({ floorIndex: linkedLevel.floorIndex, levelBelow: linkedLevel.levelBelow }, generatedLevels)
  ) {
    const {
      interiorDatumOffset,
      garageDatumOffset,
      brickLedgeDatumOffset,
      porchDatumOffset,
      garageDatum,
      porchDatum,
      deckDatum,
      deckDatumOffset,
      brickLedgeDatum,
      foundationDatum,
      foundationDatumOffset,
    } = settings;

    grade = levelInchesNew(-interiorDatumOffset);

    if (garageDatum === LevelDatumAnchor.LEVEL_GRADE) {
      garage = levelInchesNew(grade + garageDatumOffset);
    } else {
      garage = levelInchesNew(garageDatumOffset);
    }

    if (porchDatum === LevelDatumAnchor.LEVEL_GRADE) {
      porch = levelInchesNew(grade + porchDatumOffset);
    } else {
      porch = levelInchesNew(porchDatumOffset);
    }

    if (deckDatum === LevelDatumAnchor.LEVEL_GRADE) {
      deck = levelInchesNew(grade + deckDatumOffset);
    } else {
      deck = levelInchesNew(deckDatumOffset);
    }

    if (brickLedgeDatum === LevelDatumAnchor.LEVEL_GRADE) {
      wainscot = levelInchesNew(grade + brickLedgeDatumOffset);
    } else {
      wainscot = levelInchesNew(brickLedgeDatumOffset);
    }

    // foundationDatumOffset is a positive number representing a negative offset,
    // so needs to be subtracted;
    if (foundationDatum === LevelDatumAnchor.LEVEL_GRADE) {
      foundation = levelInchesNew(grade - foundationDatumOffset);
    } else {
      foundation = levelInchesNew(-foundationDatumOffset);
    }
  } else if (refLevel) {
    grade = levelInchesNew(refLevel.levelDatumsGenerated.grade - floorElevation);
    foundation = refLevel.levelDatumsGenerated.foundation;

    if (linkedLevel.floorIndex < 0) {
      wainscot = levelInchesNew(refLevel.levelDatumsGenerated.wainscot - floorElevation);
    }

    // foundationDatumOffset is a positive number representing a negative offset,
    // so needs to be subtracted;
    if (linkedLevel.floorIndex < 0 && ensureDefined(settings).basementSlabType !== SlabType.NONE) {
      assertIsNotUndefined(settings);
      const basementSettings = settings as BasementFoundationSuperSystemSettings;

      if (basementSettings.basementFoundationDatum === LevelDatumAnchor.LEVEL_GRADE) {
        foundation = levelInchesNew(grade - basementSettings.basementFoundationDatumOffset);
      } else {
        foundation = levelInchesNew(-basementSettings.basementFoundationDatumOffset);
      }
    }
  }

  return {
    instanceOffset,
    grade,
    garage,
    porch,
    deck,
    wainscot,
    foundation,
    roofBottom: levelInchesNew(roofBottom),
    roofTop: levelInchesNew(roofTop),
  };
}

function getLevelDefSettingsTable(
  settings: Table<SettingsFragment>,
  levelDefs: Table<LevelDef>,
): Table<SettingsFragment> {
  const levelUniqueIds: { [key: string]: boolean } = {};
  for (const levelId in levelDefs) {
    levelUniqueIds[levelId] = true;
  }

  return objectKeyFilter(settings, (key) => {
    return settings[key].parentId !== undefined && levelUniqueIds[settings[key].parentId];
  });
}

export function calcLevelFloorSettings(
  settingsTable: Table<SettingsFragment>,
  levels: Table<LevelDef>,
  settings: Readonly<HomeSettingsRW>,
): Table<FloorSettings> {
  const levelSettingsTable = getLevelDefSettingsTable(settingsTable, levels);

  const levelsFloorSettings: RWTable<FloorSettings> = {};

  for (const levelId in levels) {
    const finalSettings = settingsFromLevel(levelId, { ...settings }, levelSettingsTable);
    levelsFloorSettings[levelId] = finalSettings.floor;
  }

  return levelsFloorSettings as Table<FloorSettings>;
}
